import { debounce, round } from "lodash-es";
import {
  nutritionImageParseRequestPath,
  nutritionImageParseRequestsPath,
  guessAttributesIngredientsPath,
  warningsIngredientsPath,
} from "routes.js";

import Rails from "@rails/ujs";

import { onViewLoad } from "jose_modern/on_view_load.js";
import { Flash } from "jose_modern/flash.js";
import { Modal } from "jose_modern/modal.js";
import { scanHints } from "jose_modern/hint.js";
import { once } from "jose_modern/once.js";
import { setFieldWarnings } from "jose_modern/field_with_warnings.js";

import { rdvTables } from "./rdv_tables.js.erb";
import { AllergenInput } from "../allergens/common.js";
import { appendScript } from "../shared/requests";

const IMAGE_PARSE_MAXIMUM_WAIT_SECONDS = 30;
const IMAGE_PARSE_BACKOFF_SECONDS = 1;

const DV_TEXT = {
  fda: "FDA",
  canada: "Canada",
  legacyFda: "USDA (Old FDA)",
};

function init() {
  const isSubrecipe = !document.querySelector("#vitamin-input-options");

  bindMeasures();
  // Safety edit pages also lack #vitamin-input-options, so wire the safety examples
  // and the unit/amount toggle before the early return below.
  bindSafetyExamples();
  bindSafetyUnitChange();
  toggleSafetyAmountField();
  bindSafetyChangeConfirm();
  bindClearUnsavedChangesOnSubmit();
  if (isSubrecipe) return;

  bindFoodToggles();
  bindAllergens();
  bindAllergenSubcategoryGuard();
  bindBionengineered();
  bindVitamins();
  bindSpecialVitamins();
  bindSubingredientModal();
  bindAttributeGuesser();
  bindImageParser();
  bindNutrientWarnings();
}

const SERVING_FIELDS = ["nutrition_quantity", "nutrition_unit"];

const selectedFood = () =>
  document.querySelector("input[name='ingredient[food]']:checked")?.value ?? "true";

// The rules live on the server so they stay in one place as more warnings are added.
function bindNutrientWarnings() {
  const form = document.querySelector("form[data-nutrient-fields]");
  if (!form) return;

  // Nutrient values block a new ingredient, so they can only be warned about on an existing one.
  const ingredientId = form.dataset.ingredientId;
  const watchedFields = ingredientId ? JSON.parse(form.dataset.nutrientFields) : SERVING_FIELDS;

  const inputs = new Map(
    watchedFields
      .map((field) => [field, form.querySelector(`#ingredient_${field}`)])
      .filter(([, input]) => input)
  );

  once(inputs.get("nutrition_quantity"), "nutrient-warnings", () => {
    let warnedFields = [];
    let latestRequestId = 0;

    const refresh = debounce(async () => {
      const requestId = ++latestRequestId;
      const formData = new FormData();
      const food = selectedFood();
      formData.append("ingredient[food]", food);
      if (ingredientId) formData.append("id", ingredientId);
      inputs.forEach((input, field) => formData.append(`ingredient[${field}]`, input.value));

      const resp = await fetch(warningsIngredientsPath(), {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      if (resp.status !== 200) return;

      const { warnings } = await resp.json();
      if (requestId !== latestRequestId) return;

      warnedFields.forEach((field) => setFieldWarnings(inputs.get(field), []));
      warnedFields = Object.keys(warnings);
      warnedFields.forEach((field) => setFieldWarnings(inputs.get(field), warnings[field]));
    }, 300);

    inputs.forEach((input) => input.addEventListener("input", refresh));

    // The food toggle rewrites the nutrition quantity without firing an input event.
    document.querySelectorAll("input[name='ingredient[food]']").forEach((radio) => {
      radio.addEventListener("change", refresh);
    });

    refresh();
  });
}

// The food and safety unit selects share name="ingredient[nutrition_unit]".
// Disable the inactive one so the POST carries exactly one value.
function toggleSafetyUnitField(enable) {
  const wrapper = document.querySelector("#safety-fields");
  if (!wrapper) return;
  wrapper.classList.toggle("is-hidden", !enable);
  wrapper.querySelectorAll("[data-safety-unit-select]").forEach((el) => {
    el.disabled = !enable;
  });
  wrapper.querySelectorAll("[data-safety-amount-input]").forEach((el) => {
    el.disabled = !enable;
  });

  const foodUnit = document.getElementById("ingredient_nutrition_unit");
  if (foodUnit) foodUnit.disabled = enable;

  const examplesColumn = document.querySelector("#safety-examples-column");
  if (examplesColumn) examplesColumn.classList.toggle("is-hidden", !enable);

  const tutorialBtn = document.querySelector("#ingredient-form-tutorial-button");
  if (tutorialBtn) tutorialBtn.classList.toggle("is-hidden", enable);

  const heading = document.querySelector("#general-information-heading");
  if (heading) heading.classList.toggle("is-hidden", enable);

  const nameWrapper = document.querySelector("#ingredient-name-wrapper");
  const target = document.querySelector(
    enable ? "#safety-name-anchor" : "#general-info-name-anchor"
  );
  if (nameWrapper && target && nameWrapper.parentElement !== target) {
    target.appendChild(nameWrapper);
  }

  if (enable) toggleSafetyAmountField();
}

// pass_fail steps record their amount per production run, not on the ingredient;
// the safety_amount_forbidden_for_pass_fail validation rejects any value sent here.
function toggleSafetyAmountField() {
  const unitSelect = document.querySelector("[data-safety-unit-select]");
  if (!unitSelect || unitSelect.disabled) return;

  const isPassFail = unitSelect.value === "pass_fail";
  document.querySelectorAll("[data-safety-amount-field]").forEach((el) => {
    el.classList.toggle("is-hidden", isPassFail);
  });
  document.querySelectorAll("[data-safety-amount-input]").forEach((el) => {
    el.disabled = isPassFail;
    if (isPassFail) el.value = "";
  });
}

function bindSafetyUnitChange() {
  document.querySelectorAll("[data-safety-unit-select]").forEach((select) => {
    select.addEventListener("change", () => toggleSafetyAmountField());
  });
}

// The "include the brand" name guidance only applies to food ingredients.
function toggleNameDetailHelp(show) {
  const help = document.querySelector("[data-name-detail-help]");
  if (help) help.classList.toggle("is-hidden", !show);
}

// On a safety ingredient edit, changing the name or target propagates to every
// recipe using it. Confirm against the affected-recipes list before saving.
function bindSafetyChangeConfirm() {
  const modal = document.getElementById("safety-change-confirm-modal");
  if (!modal) return;

  const nameInput = document.getElementById("ingredient_name");
  const form = nameInput?.closest("form");
  if (!form) return;

  const amountInput = document.getElementById("ingredient_safety_amount");
  const initialName = nameInput.value;
  const initialAmount = amountInput ? amountInput.value : "";
  let confirmed = false;

  form.addEventListener("submit", (event) => {
    if (confirmed) return;

    const changed =
      nameInput.value !== initialName || (amountInput && amountInput.value !== initialAmount);
    if (!changed) return;

    event.preventDefault();

    const renamed = nameInput.value !== initialName;
    const currentAmount = amountInput ? amountInput.value.trim() : "";
    const settingTarget = currentAmount !== "" && currentAmount !== initialAmount.trim();
    const clearingTarget = initialAmount.trim() !== "" && currentAmount === "";

    let appliesVariant = null;
    if (renamed && settingTarget) appliesVariant = "rename-and-target";
    else if (renamed) appliesVariant = "rename";
    else if (settingTarget) appliesVariant = "target";

    modal.querySelectorAll("[data-safety-applies]").forEach((el) => {
      el.classList.toggle("is-hidden", el.dataset.safetyApplies !== appliesVariant);
    });
    modal
      .querySelector("[data-safety-clearing-note]")
      ?.classList.toggle("is-hidden", !clearingTarget);
    modal
      .querySelector("[data-safety-production-runs-note]")
      ?.classList.toggle("is-hidden", !settingTarget);

    Modal.show("#safety-change-confirm-modal");
  });

  // Showing the modal prevents the submit, but UJS already disabled the button via
  // data-disable-with; re-enable on close so it isn't stuck disabled after a dismiss.
  modal.addEventListener("modal:close", () => Rails.enableElement(form));

  modal.querySelector("[data-safety-change-confirm]")?.addEventListener("click", () => {
    confirmed = true;
    Modal.hide("#safety-change-confirm-modal");
    form.requestSubmit();
    // Reset so a submit blocked by native validation re-confirms on the next attempt.
    confirmed = false;
  });
}

// Listens on document so submits blocked earlier (preventDefault/stopPropagation by the
// allergen guard or safety confirm) keep their unsaved-changes warning. Uses the global because
// a direct import from recipal_app gets a separate module copy that doesn't track these inputs.
function bindClearUnsavedChangesOnSubmit() {
  const form = document.querySelector("form[data-warn-unsaved-changes][data-ingredient-id]");
  if (!form) return;

  document.addEventListener("submit", (event) => {
    if (event.target !== form || event.defaultPrevented) return;

    Array.from(form.elements).forEach((el) => window.WarnUnsavedChanges.markClean(el));
  });
}

// Every ingredient type carries a brand except safety steps.
function toggleBrandField(show) {
  const field = document.querySelector("#brand-field");
  if (field) field.classList.toggle("is-hidden", !show);
}

function bindFoodToggles() {
  // Handles toggling food/non-food ingredient option
  document.querySelectorAll("input[name='ingredient[food]']").forEach((input) => {
    input.addEventListener("change", (event) => {
      if (event.target.value === "true") {
        // Food Ingredient
        // Hide and unset non-food type sub options
        document.querySelector("#non-food-options").classList.add("is-hidden");
        document.querySelectorAll("input[name='ingredient[non_food]']").forEach((input) => {
          input.checked = false;
        });
        toggleSafetyUnitField(false);
        toggleNameDetailHelp(true);
        toggleBrandField(true);

        // Show food fields
        document.querySelector("#food-fields").classList.remove("is-hidden");
        document.querySelector("#food-fields [data-measures-form]")?.removeAttribute("disabled");
        document.querySelector("#non-food-fields")?.classList.add("is-hidden");
        document
          .querySelector("#non-food-fields [data-measures-form]")
          ?.setAttribute("disabled", "");
        document.querySelector("#ai-upload-section")?.classList.remove("is-hidden");
        document.querySelector("#food-only-fields")?.classList.remove("is-hidden");

        // Clear the food number inputs that the non-food branch sets to 0.
        document.querySelectorAll("#food-fields input[type='number']").forEach((input) => {
          input.value = "";
        });

        // Re-hide submit if haven't chosen vitamin type yet
        if (document.querySelector("#vitamin-input-options").value === "") {
          document.querySelectorAll(".ingredient-submit-button").forEach((el) => {
            el.classList.add("is-hidden");
          });
        }
      } else {
        // Non-Food Ingredient
        // Show non-food type sub options
        document.querySelector("#non-food-options").classList.remove("is-hidden");
        // Default to labor
        document.querySelector("#ingredient_non_food_labor").checked = true;
        document.querySelector("#ingredient_non_food_labor").dispatchEvent(new Event("change"));
        toggleNameDetailHelp(false);

        // Hide food fields, show non-food measures
        document.querySelector("#food-fields").classList.add("is-hidden");
        document.querySelector("#food-fields [data-measures-form]")?.setAttribute("disabled", "");
        document.querySelector("#non-food-fields")?.classList.remove("is-hidden");
        document
          .querySelector("#non-food-fields [data-measures-form]")
          ?.removeAttribute("disabled");
        document.querySelector("#ai-upload-section")?.classList.add("is-hidden");
        document.querySelector("#food-only-fields")?.classList.add("is-hidden");

        // Show submit button
        document.querySelectorAll(".ingredient-submit-button").forEach((el) => {
          el.classList.remove("is-hidden");
        });

        // Set food number inputs to 0 to satisfy `required` for non-food submit.
        document.querySelectorAll("#food-fields input[type='number']").forEach((input) => {
          // Skip measure templates -- clobbering them creates phantom measure submissions.
          if (input.closest("#additional-measure-template, #additional-volume-measure-template"))
            return;

          input.value = 0;
        });
      }
    });
  });

  // Handles toggling non-food ingredient sub-options
  document.querySelectorAll("input[name='ingredient[non_food]']").forEach((input) => {
    input.addEventListener("change", (event) => {
      document.querySelectorAll("[data-hint-option]").forEach((hintEl) => {
        if (hintEl.dataset["hintOption"] === event.target.value) {
          hintEl.classList.remove("is-hidden");
        } else {
          hintEl.classList.add("is-hidden");
        }
      });

      const defaultHint = document.getElementById("non-food-default-hint");
      if (defaultHint) {
        defaultHint.classList.toggle("is-hidden", event.target.value === "safety");
      }

      toggleSafetyUnitField(event.target.value === "safety");
      toggleBrandField(event.target.value !== "safety");
      toggleNonFoodMeasures(event.target.value);
      syncNonFoodMeasureUnits(event.target.value);
    });
  });

  // Now that the listeners are set up, trigger a change event to show proper hint
  // This will only be checked on an ingredient that is already non_food
  document
    .querySelector("input[name='ingredient[non_food]']:checked")
    ?.dispatchEvent(new Event("change"));
}

function toggleNonFoodMeasures(type) {
  const section = document.querySelector("#non-food-fields");
  if (!section) return;

  const isSafety = type === "safety";
  section.classList.toggle("is-hidden", isSafety);

  const fieldset = section.querySelector("[data-measures-form]");
  if (fieldset) fieldset.disabled = isSafety;
}

// Ensure the measure units match the food type
function syncNonFoodMeasureUnits(type) {
  const fieldset = document.querySelector("#non-food-fields [data-measures-form]");
  if (!fieldset) return;

  const unitsByType = JSON.parse(fieldset.dataset["nonFoodUnits"] || "{}");
  const units = unitsByType[type];
  // Safety has no measures section; leave the dropdowns untouched.
  if (!units) return;

  const defaultUnits = fieldset.querySelector("[data-non-food-default-units]");
  if (defaultUnits) defaultUnits.textContent = units.join(", ");

  fieldset.querySelectorAll("select[id$='_equivalent_unit']").forEach((select) => {
    const previous = select.value;
    select.innerHTML = "";
    units.forEach((unit) => {
      const option = document.createElement("option");
      option.value = unit;
      option.textContent = unit;
      select.appendChild(option);
    });
    select.value = units.includes(previous) ? previous : units[0];
  });
}

function percentFromValue(value, vitamin, vitaminType) {
  const rdvTable = rdvTables[vitaminType];
  return round((value / rdvTable[vitamin]) * 100, 2);
}

function bindVitamins() {
  const vitaminValueFields = document.querySelectorAll(".vitamin-input-value");
  const vitaminPercentFields = document.querySelectorAll(".vitamin-input-percent");
  const vitaminPercentLabels = document.querySelectorAll(".vitamin-input-percent label");

  const vitaminPercentInputs = document.querySelectorAll(".vitamin-input-percent input");
  const vitaminValueInputs = document.querySelectorAll(".vitamin-input-value input");

  // Handles changing what form the vitamins are entered (value, % fda, % ca, ...)
  //
  // It is important that the 'value' input is only sync'd to the 'percent' input when
  // this select option changes.
  // 'percent' inputs sync to 'value' on change, so they would loop otherwise.
  // Only the 'value' inputs are sent to the server so the 'percent' inputs can be lazy updates
  document.querySelector("#vitamin-input-options").addEventListener("change", (event) => {
    const vitaminType = event.target.value;
    // Hide/Show all of the vitamin fields when input type is selected
    if (vitaminType === "") {
      // No vitamin type is selected, hide all the vitamin fields
      document.querySelector("#vitamin-select-required").classList.add("is-hidden");
      document.querySelectorAll(".ingredient-submit-button").forEach((el) => {
        el.classList.add("is-hidden");
      });
      // Return early if they didn't actually select a vitamin type
      return;
    } else {
      // input type was selected, show vitamin fields
      document.querySelector("#vitamin-select-required").classList.remove("is-hidden");
      document.querySelectorAll(".ingredient-submit-button").forEach((el) => {
        el.classList.remove("is-hidden");
      });
    }

    if (vitaminType == "value") {
      vitaminValueFields.forEach((field) => field.classList.remove("is-hidden"));
      vitaminPercentFields.forEach((field) => field.classList.add("is-hidden"));
    } else {
      vitaminPercentInputs.forEach((percentInput) => {
        const vitamin = percentInput.id.split("_percent")[0];
        const valueInput = document.querySelector(`#ingredient_${vitamin}`);
        const valueField = valueInput.closest(".vitamin-input-value");
        const percentField = percentInput.closest(".vitamin-input-percent");

        const rdvTable = rdvTables[vitaminType];
        const hasRdv = rdvTable[vitamin] !== undefined;

        if (hasRdv) {
          // Has RDV - show percent field, hide value field
          valueField.classList.add("is-hidden");
          percentField.classList.remove("is-hidden");

          // Sync value to percent
          if (valueInput.value === "") {
            percentInput.value = "";
          } else {
            percentInput.value = percentFromValue(valueInput.value, vitamin, vitaminType);
          }
        } else {
          // No RDV - show value field (default unit), hide percent field
          valueField.classList.remove("is-hidden");
          percentField.classList.add("is-hidden");
        }

        // Update hint texts
        percentInput.dispatchEvent(new Event("input"));
      });

      // Update labels for percent fields
      vitaminPercentLabels.forEach((label) => {
        label.textContent = label.textContent.replace(/\(.*\)/, `(% ${DV_TEXT[vitaminType]})`);
      });
    }
  });

  // Handles when changing a value in any 'percent' input
  // Only 'value' inputs are sent to the server, so we must always sync 'percent' -> 'value'
  vitaminPercentInputs.forEach((percentInput) => {
    const vitamin = percentInput.id.split("_percent")[0];

    const valueFromPercent = (percent) => {
      const rdvTable = rdvTables[document.querySelector("#vitamin-input-options").value];
      return round((percent * rdvTable[vitamin]) / 100, 2);
    };

    percentInput.addEventListener("change", (event) => {
      const valueInput = document.querySelector(`#ingredient_${vitamin}`);
      if (percentInput.value === "") {
        valueInput.value = "";
      } else {
        valueInput.value = valueFromPercent(event.target.value);
      }

      // There are some special fields that have change listeners that need to be fired
      // This would be a problem if the 'value' inputs updated the 'percent' inputs
      // on 'change', but they only update 'percent' inputs when the #vitamin-input-options
      // select is changed
      valueInput.dispatchEvent(new Event("change"));
      // Update value hint texts
      valueInput.dispatchEvent(new Event("input"));
    });

    // Update percent hint texts on input instead of on change
    percentInput.addEventListener("input", (event) => {
      const hintEl = document.querySelector(`[data-nutrient-hint='${vitamin}_percent']`);
      if (event.target.value === "") {
        hintEl.textContent = "";
      } else {
        const value = valueFromPercent(Number(event.target.value));
        hintEl.textContent = `${value} ${hintEl.dataset["unit"]}`;
      }
    });
  });

  vitaminValueInputs.forEach((valueInput) => {
    const vitamin = valueInput.id.split("ingredient_")[1];
    const hintEl = document.querySelector(`[data-nutrient-hint='${vitamin}_value']`);
    if (!hintEl) {
      return;
    }

    const vitaminType = hintEl.dataset["rdv"];

    valueInput.addEventListener("input", (event) => {
      if (event.target.value === "") {
        hintEl.textContent = "";
      } else {
        const percent = percentFromValue(Number(valueInput.value), vitamin, vitaminType);
        hintEl.textContent = `${percent}% ${DV_TEXT[vitaminType]}`;
      }
    });
  });
}

function bindSpecialVitamins() {
  // Vitamin D/Vitamin A have two options for input by value - IU/mcg
  // mcg field is primary (submits to server)

  // Vitamin A: one-way binding (IU → mcg only, mcg clears IU)
  {
    const iuInput = document.getElementById("ingredient_vitamin_a_IU");
    const mcgInput = document.getElementById("ingredient_vitamin_a");
    const iuToMcg = 0.3; // 1 IU = 0.3 mcg RAE

    let settingFromIU = false;

    // When IU changes, calculate and set mcg
    iuInput.addEventListener("input", () => {
      if (iuInput.value === "") {
        mcgInput.value = "";
      } else {
        mcgInput.value = round(iuInput.value * iuToMcg, 2);
      }

      settingFromIU = true;
      mcgInput.dispatchEvent(new Event("input")); // update hints
      settingFromIU = false;
    });

    // When user types in mcg, clear IU (one-way)
    mcgInput.addEventListener("input", () => {
      if (!settingFromIU) {
        iuInput.value = "";
      }
    });
  }

  // Vitamin D: two-way binding (IU ↔ mcg)
  {
    const iuInput = document.getElementById("ingredient_vitamin_d_IU");
    const mcgInput = document.getElementById("ingredient_vitamin_d");
    const iuToMcg = 0.025; // 1 IU = 0.025 mcg
    const mcgToIU = 40; // 1 mcg = 40 IU

    let settingFromIU = false;
    let settingFromMcg = false;

    // When IU changes, calculate and set mcg
    iuInput.addEventListener("input", () => {
      if (settingFromMcg) return;

      if (iuInput.value === "") {
        mcgInput.value = "";
      } else {
        mcgInput.value = round(iuInput.value * iuToMcg, 2);
      }

      settingFromIU = true;
      mcgInput.dispatchEvent(new Event("input")); // update hints
      settingFromIU = false;
    });

    // When user types in mcg, calculate and set IU (two-way)
    mcgInput.addEventListener("input", () => {
      if (settingFromIU) return;

      if (mcgInput.value === "") {
        iuInput.value = "";
      } else {
        settingFromMcg = true;
        iuInput.value = round(mcgInput.value * mcgToIU, 2);
        settingFromMcg = false;
      }
    });

    // Initialize IU field from mcg value on page load
    if (mcgInput.value !== "") {
      mcgInput.dispatchEvent(new Event("input"));
    }
  }
}

function bindMeasures() {
  // Handles 'additional measures at bottom' link
  // Bottom measures aren't visible if no vitamin type is selected, this selects a type
  document.querySelector("#measures-section-redirect")?.addEventListener("click", () => {
    const vitaminToggle = document.querySelector("#vitamin-input-options");
    if (vitaminToggle.value === "") {
      vitaminToggle.value = "value";
      vitaminToggle.dispatchEvent(new Event("change"));
    }
  });

  // Handles adding a blank volume measure form (food only)
  document.querySelector("#add-volume-measure")?.addEventListener("click", (event) => {
    event.preventDefault();

    const clone = cloneTemplate("#additional-volume-measure-template");
    scanHints(clone);
    document.querySelector("#volume-measure-bottom-anchor").appendChild(clone);
  });

  // Handles adding a blank weight measure form
  document.querySelector("#add-measure")?.addEventListener("click", (event) => {
    event.preventDefault();

    let clone = cloneTemplate("#additional-measure-template");
    document.querySelector("#weight-measure-bottom-anchor").appendChild(clone);
  });

  // Handles adding a blank measure form on non-food ingredients
  document.querySelector("#add-non-food-measure")?.addEventListener("click", (event) => {
    event.preventDefault();

    let clone = cloneTemplate("#additional-non-food-measure-template");
    document.querySelector("#non-food-weight-measure-bottom-anchor").appendChild(clone);
  });

  // The top row of volume measure forms are hidden when they're being used as the nutrition_unit
  // The following functions keep the field in sync with nutrition_quantity/nutrition_unit
  // since the user can't adjust the hidden field

  // Sync nutrition quantity field with top volume measure quantity when there is one
  document.querySelector("#ingredient_nutrition_quantity")?.addEventListener("change", (event) => {
    const hiddenQuantityInput = document.querySelector(
      "#volume-measure-top-anchor input[id$='_quantity']"
    );

    if (hiddenQuantityInput) {
      hiddenQuantityInput.value = event.target.value;
    }
  });

  // When a user changes the nutrition_unit
  // if it's a volume
  //    create blank or move existing volume measure to the top
  //    hide top row and sync unit/volume_type values
  // if it's not a volume
  //    move any volume measure form that was at the top back to the volume measure section
  document.querySelector("#ingredient_nutrition_unit")?.addEventListener("change", (event) => {
    const selected = event.target.options[event.target.selectedIndex];
    const volumeType = selected.dataset["volumeType"];

    const moveFormToBottom = (form) => {
      if (form.dataset["volumeType"] === "") {
        document.querySelector("#volume-measure-bottom-anchor").prepend(form);
      } else {
        document.querySelector("#volume-measure-bottom-anchor").append(form);
      }

      document.querySelector("#volume-measure-top").classList.add("is-hidden");
      document.querySelector("#nutrition-unit-volume-measure-notice").classList.add("is-hidden");

      form.querySelector(".volume-measure-delete").classList.remove("is-hidden");
      form.querySelector("[data-volume-top-row]").classList.remove("is-hidden");
    };

    const moveFormToTop = (form) => {
      document.querySelector("#volume-measure-top-anchor").append(form);

      document.querySelector("#volume-measure-top").classList.remove("is-hidden");
      document.querySelector("#nutrition-unit-volume-measure-notice").classList.remove("is-hidden");

      form.querySelector(".volume-measure-delete").classList.add("is-hidden");
      form.querySelector("[data-volume-top-row]").classList.add("is-hidden");
    };

    const existingTopForm = document.querySelector(
      "#volume-measure-top-anchor [data-measure-fields]"
    );

    // volumeType will be a string if a volume is selected
    if (volumeType === undefined) {
      if (existingTopForm) {
        moveFormToBottom(existingTopForm);
      }
      return;
    }

    let measureForm = document.querySelector(
      `[data-measure-fields][data-template='false'][data-volume-type='${volumeType}']`
    );

    // If an existing measure doesn't exist for this volume create one
    if (!measureForm) {
      measureForm = cloneTemplate("#additional-volume-measure-template");
      measureForm.dataset["volumeType"] = volumeType;

      // Sync volume_type which will be hidden
      const volumeTypeInput = measureForm.querySelector('[id$="_volume_type"]');
      volumeTypeInput.value = volumeType;
    }
    // Sync nutrition quantity/unit since they're hidden
    measureForm.querySelector('[id$="_unit"]').value = selected.value.split(",")[0];
    measureForm.querySelector('[id$="_quantity"]').value = document.querySelector(
      "#ingredient_nutrition_quantity"
    ).value;

    if (existingTopForm && existingTopForm !== measureForm) {
      moveFormToBottom(existingTopForm);
    }

    moveFormToTop(measureForm);
  });
}

function cloneTemplate(templateId) {
  // This function sets the 'index' of the cloned node. Because of this, the return value
  // must be placed in the DOM (or ignored) before it is called again or the next clone will
  // have the same index
  let clone = document.querySelector(templateId).children[0].cloneNode(true);

  let inputs = clone.querySelectorAll("input, select, label");
  const measureIndex = document.querySelectorAll(
    "[data-measure-fields][data-template='false']"
  ).length;

  inputs.forEach(function (input) {
    // Update the 'name' attribute
    if (input.name) {
      input.name = input.name.replace(/\[\d+\]/, "[" + measureIndex + "]");
    }
    // Update the 'id' attribute
    if (input.id) {
      input.id = input.id.replace(/_\d+_/, "_" + measureIndex + "_");
    }
    // Update the 'for' attribute on labels
    if (input.htmlFor) {
      input.htmlFor = input.htmlFor.replace(/_\d+_/, "_" + measureIndex + "_");
    }
  });

  clone.dataset["template"] = false;
  return clone;
}

function bindAllergens() {
  const updateHiddenField = (allergens) => {
    document.querySelector('input[name="ingredient[allergens]"]').value = JSON.stringify(allergens);
  };

  document.querySelectorAll(".ingredient-allergens").forEach((container) => {
    const input = new AllergenInput(container, (allergens) => {
      updateHiddenField(allergens);
    });

    bindAllergenChangeWatchers(container);

    // Ensure that the hidden field is initially populated
    updateHiddenField(input.collectAllergens());
  });
}

function bindAllergenChangeWatchers(container) {
  // The container outlives the autofill editor swap, so bind these once, not on every re-run.
  if (container.dataset.allergenWatchersBound) return;
  container.dataset.allergenWatchersBound = "true";

  // collect() drops incomplete majors, so a bare-major check doesn't trigger onUpdate -- mark dirty
  // on any change so guess_attributes doesn't replace the editor while the user picks a subtype.
  container.addEventListener("change", markAllergenInputDirty);

  AllergenInput.bindSettleWarning(container);
}

function markAllergenInputDirty() {
  document.querySelector("#allergen_input_dirty").value = "true";
}

function bindAllergenSubcategoryGuard() {
  const form = document.querySelector(".ingredient-allergens")?.closest("form");
  if (!form) return;

  form.addEventListener("submit", (event) => {
    // Non-food submits hide the allergen editor and don't save it, so don't block them.
    const foodChoice = document.querySelector("input[name='ingredient[food]']:checked");
    if (foodChoice && foodChoice.value !== "true") return;

    // The autofill guess replaces the editor node, so re-query it at submit time.
    const editor = document.querySelector(".ingredient-allergens .allergen-editor");
    if (!editor) return;
    if (AllergenInput.incompleteMajors(editor).length === 0) return;

    event.preventDefault();
    // Stop the blocked submit from reaching UJS, which would disable the submit button and never
    // re-enable it (no ajax:complete fires) -- leaving the form unsubmittable.
    event.stopPropagation();
    AllergenInput.showIncompleteWarning(editor);
    AllergenInput.scrollWarningIntoView(editor);
  });
}

function bindBionengineered() {
  const bioengineeredInput = document.querySelector("#ingredient_bioengineered");
  bioengineeredInput.addEventListener("change", () => {
    document.querySelector("#bioengineered_input_dirty").value = "true";
  });
}

function bindAttributeGuesser() {
  const ingredientListInput = document.querySelector("#ingredient_ingredient_list");
  const debounced = debounce(guessAttributes, 500);

  ingredientListInput.addEventListener("keypress", debounced);
  ingredientListInput.addEventListener("change", guessAttributes);
}

async function guessAttributes() {
  const ingredientListInput = document.querySelector("#ingredient_ingredient_list");
  const ingredients = ingredientListInput.value;
  const formData = new FormData();
  formData.append("ingredients", ingredients);

  const resp = await fetch(guessAttributesIngredientsPath(), {
    method: "POST",
    headers: {
      Accept: "application/javascript",
    },
    body: formData,
  });
  if (resp.status !== 200) {
    return;
  }
  const script = await resp.text();
  appendScript(script);
  bindAllergens();
}

function bindImageParser() {
  const attachmentInput = document.querySelector("#ingredient_source_attachment");
  const ingredientForm = attachmentInput.form;
  const parseUploadButton = document.querySelector("#parseUploadButton");

  // Subscriber only feature
  if (!parseUploadButton) return;

  // Some controls are disabled because we specifically do not want to submit them in
  // certain situations. Remember which were disabled before so they can stay disabled afterwards
  let preDisabledSnapshot = null;
  const toggleForm = (enabled) => {
    const els = ingredientForm.querySelectorAll("input, button, select");
    if (!enabled) {
      preDisabledSnapshot = new Set();
      els.forEach((el) => {
        if (el.disabled) preDisabledSnapshot.add(el);
        el.disabled = true;
      });
    } else {
      els.forEach((el) => {
        el.disabled = preDisabledSnapshot.has(el);
      });
    }
  };

  attachmentInput.addEventListener("change", async (e) => {
    parseUploadButton.disabled = e.target.files.length === 0;
  });

  parseUploadButton.addEventListener("click", async () => {
    if (!attachmentInput.files.length) return;

    toggleForm(false);

    Flash.notice(
      `Reading the image and extracting the ingredient details. This may take up to ${IMAGE_PARSE_MAXIMUM_WAIT_SECONDS} seconds.`,
      {
        duration: IMAGE_PARSE_MAXIMUM_WAIT_SECONDS * 1000,
      }
    );

    try {
      const file = attachmentInput.files[0];
      const id = await postImageData(file);
      await pollAndPopulateResults(id);

      guessAttributes();
    } catch (error) {
      console.error(error);
      Flash.hideExisting();
      Flash.error(
        "We were unable to pull details from the image. Please enter the ingredient details manually."
      );
    } finally {
      toggleForm(true);
    }

    const vitaminToggle = document.querySelector("#vitamin-input-options");
    vitaminToggle.value = "value";
    vitaminToggle.dispatchEvent(new Event("change"));
  });
}

async function postImageData(file) {
  const formData = new FormData();
  formData.append("file", file);

  const resp = await fetch(nutritionImageParseRequestsPath(), {
    method: "POST",
    headers: {
      Accept: "application/json",
    },
    body: formData,
  });

  if (resp.status !== 201) {
    throw new Error("Failed to process image");
  }

  const { id } = await resp.json();

  return id;
}

async function pollAndPopulateResults(id) {
  const maxAttempts = IMAGE_PARSE_MAXIMUM_WAIT_SECONDS / IMAGE_PARSE_BACKOFF_SECONDS;
  let attempts = 0;

  while (attempts < maxAttempts) {
    const resp = await fetch(nutritionImageParseRequestPath({ id }), {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    if (resp.status !== 200) {
      throw new Error("Failed to process image");
    }

    const { status, parsed } = await resp.json();

    switch (status) {
      case "pending":
      case "in_progress":
        attempts += 1;
        await new Promise((resolve) => setTimeout(resolve, IMAGE_PARSE_BACKOFF_SECONDS * 1000));
        break;

      case "complete":
        populateForm(parsed);
        return;

      default:
        throw new Error(`Image parse request failed: ${status}`);
    }
  }

  throw new Error("Image parse request timed out");
}

function populateForm(parsedData) {
  Object.entries(parsedData).forEach(([key, value]) => {
    const input = document.querySelector(`#ingredient_${key}`);
    if (!input) return;

    if (key === "bioengineered") {
      input.checked = value;
    } else if (key === "allergens") {
      input.value = JSON.stringify(value);
    } else {
      input.value = value;
    }

    if (key === "ingredient_list") {
      input.dispatchEvent(new Event("input"));
    }
  });

  Flash.hideExisting();
  Flash.success("Ingredient details updated from image. Please review the results.");
}

function bindSubingredientModal() {
  // Modal that pops up if a user enters a comma in their ingredient list without
  // it being in backets
  document.querySelector("#ingredient_ingredient_list").addEventListener("change", (event) => {
    const ing_list_split = event.target.value.split(",");
    // look for brackets/parens of some sort
    if (ing_list_split.length > 1 && ing_list_split[0].search(/[([{]/) < 0) {
      // if has commas without parens/brackets before, show warning
      Modal.show("#subingredients-warning-modal");
    }
  });
}

function applyExampleRowToForm(row) {
  const nameInput = document.getElementById("ingredient_name");
  if (nameInput) {
    nameInput.value = row.dataset.name;
    nameInput.dispatchEvent(new Event("input", { bubbles: true }));
    nameInput.dispatchEvent(new Event("change", { bubbles: true }));
  }
  const unitSelect = document.getElementById("ingredient_nutrition_unit_safety");
  if (unitSelect) {
    unitSelect.value = row.dataset.unit;
    unitSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

// The examples panel reparents between the desktop column and the modal.
// Document-level delegation keeps handlers wired across the move; once()
// prevents duplicate bindings on repeat init calls.
function bindSafetyExamples() {
  const panel = document.getElementById("safety-examples-panel");
  if (!panel) return;

  once(document, "safetyExamplesBound", () => {
    const inlineSlot = document.getElementById("safety-examples-column");
    const modal = document.getElementById("safety-examples-modal");
    const modalBody = document.getElementById("safety-examples-modal-body");

    const filterRows = debounce(() => {
      const livePanel = document.getElementById("safety-examples-panel");
      if (!livePanel) return;
      const search = livePanel.querySelector("[data-safety-examples-search]");
      const unitFilter = livePanel.querySelector("[data-safety-examples-unit-filter]");
      const q = (search?.value || "").trim().toLowerCase();
      const u = unitFilter?.value || "";

      livePanel.querySelectorAll("[data-safety-example-row]").forEach((row) => {
        const matchesUnit = !u || row.dataset.unit === u;
        const matchesQuery =
          !q ||
          row.dataset.keywords.includes(q) ||
          row.dataset.unitLabel.toLowerCase().includes(q) ||
          row.dataset.unit.includes(q);
        row.classList.toggle("is-hidden", !(matchesUnit && matchesQuery));
      });
    }, 100);

    document.addEventListener("input", (event) => {
      if (event.target.matches?.("[data-safety-examples-search]")) filterRows();
    });

    document.addEventListener("change", (event) => {
      if (event.target.matches?.("[data-safety-examples-unit-filter]")) filterRows();
    });

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest?.("[data-safety-examples-trigger]");
      if (trigger && modal && modalBody) {
        const livePanel = document.getElementById("safety-examples-panel");
        if (livePanel && livePanel.parentElement !== modalBody) {
          modalBody.appendChild(livePanel);
        }
        Modal.show("#safety-examples-modal");
        return;
      }

      const row = event.target.closest?.("[data-safety-example-row]");
      if (row) {
        applyExampleRowToForm(row);
        if (modal?.classList.contains("is-active")) {
          modal.querySelector("[data-closes]")?.click();
        }
      }
    });

    if (modal && inlineSlot) {
      new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.attributeName !== "class") return;
          if (modal.classList.contains("is-active")) return;
          const livePanel = document.getElementById("safety-examples-panel");
          if (livePanel && livePanel.parentElement !== inlineSlot) {
            inlineSlot.appendChild(livePanel);
          }
        });
      }).observe(modal, { attributes: true, attributeFilter: ["class"] });
    }
  });
}

onViewLoad("ingredients#new", init);
onViewLoad("ingredients#create", init);
onViewLoad("ingredients#edit", init);
onViewLoad("ingredients#update", init);
